import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import { BaseParentComponent } from '@components/_base/base-parent/base-parent.component';
import { displayError } from '@app/_utils/error-util';
import { UIEventCustom } from '@app/_utils/ui-event-util';
import { TechnicalPointService } from '@app/tech-points/_services/technical-point.service';
import { TechnicalPoint } from '@app/tech-points/_models/technical-point.model';

import { SmsCampaignConfigService } from '../_services/sms-campaign-config.service';
import { SmsCampaignConfig } from '../_models/sms-campaign-config.model';
import { HOURS, INCOMING_WAYS, MINUTES } from '../_enums/incoming-way.enum';

export const MESSAGE_MAX_LENGTH = 250;

@Component({
  selector: 'app-add-edit-sms-campaign',
  templateUrl: './add-edit-sms-campaign.component.html',
  styleUrls: ['./add-edit-sms-campaign.component.css']
})
export class AddEditSmsCampaignComponent extends BaseParentComponent implements OnInit {

  disableActionBtn = false;
  dataLoaded = false;

  readonly incomingWays = INCOMING_WAYS;
  readonly hours = HOURS;
  readonly minutes = MINUTES;
  readonly messageMaxLength = MESSAGE_MAX_LENGTH;

  technicalPoints: TechnicalPoint[] = [];

  mainForm = this.fb.group({
    id:              [null],
    name:            [null, [Validators.required, Validators.maxLength(255)]],
    isActive:        [true],
    technicalPoints: [[] as number[], Validators.required],
    // Required: the send job schedules each campaign at this time, so a campaign
    // without one cannot be sent.
    notifHour:       [null, Validators.required],
    notifMinute:     ['00'],
    incomingWays:    [[] as string[], Validators.required],
    messageText:     [null, [Validators.required, Validators.maxLength(MESSAGE_MAX_LENGTH)]],
    createdOn:       [null]
  });

  constructor(
    private router:                   Router,
    private route:                    ActivatedRoute,
    private fb:                       FormBuilder,
    private uiEvent:                  UIEventCustom,
    private toastr:                   ToastrService,
    private translateService:         TranslateService,
    private technicalPointService:    TechnicalPointService,
    private smsCampaignConfigService: SmsCampaignConfigService,
  ) {
    super(router);
  }

  ngOnInit() {
    this.showValidationsSubscription = this.showValidations$.subscribe({
      next: (data) => this.showValidations = data
    });

    this.technicalPointService.findTechnicalPoints().subscribe({
      next: (data) => {
        // Campaigns reference tech points by mobileAppId, so points without one
        // cannot be selected.
        this.technicalPoints = (data ?? []).filter(tp => tp.mobileAppId != null);
        this.loadCampaign();
      },
      error: (err) => {
        displayError(err);
        this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
      }
    });
  }

  get f() {
    return this.mainForm.controls;
  }

  get isEdit(): boolean {
    return !!this.mainForm.value.id;
  }

  get messageLength(): number {
    return this.f.messageText.value?.length ?? 0;
  }

  private loadCampaign() {
    this.route.queryParams.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.smsCampaignConfigService.findById(+id).subscribe({
          next: (config) => {
            const [hour, minute] = this.splitTime(config.notificationTime);
            this.mainForm.patchValue({
              id:              config.id,
              name:            config.name,
              isActive:        !!config.isActive,
              technicalPoints: (config.technicalPoints ?? []).map(tp => tp.mobileAppId),
              notifHour:       hour,
              notifMinute:     minute,
              incomingWays:    config.incomingWays ?? [],
              messageText:     config.messageText,
              createdOn:       config.createdOn
            });
            this.dataLoaded = true;
          },
          error: (err) => {
            displayError(err);
            this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
          }
        });
      } else {
        this.dataLoaded = true;
      }
    });
  }

  private splitTime(time?: string | null): [string | null, string] {
    if (!time) return [null, '00'];
    const [hour, minute] = time.split(':');
    return [hour ?? null, minute ?? '00'];
  }

  private joinTime(): string | null {
    const hour = this.mainForm.value.notifHour;
    if (!hour) return null;
    const minute = this.mainForm.value.notifMinute || '00';
    return `${hour}:${minute}`;
  }

  save() {
    if (!this.mainForm.valid) {
      this.uiEvent.displayUIError();
      this.showValidationsSubject.next(true);
      return;
    }

    const formValue = this.mainForm.value;
    const payload: SmsCampaignConfig = {
      id:               formValue.id ?? undefined,
      name:             formValue.name,
      isActive:         !!formValue.isActive,
      notificationTime: this.joinTime(),
      messageText:      formValue.messageText,
      technicalPoints:  (formValue.technicalPoints ?? []).map((mobileAppId: number) => ({ mobileAppId })),
      incomingWays:     formValue.incomingWays ?? [],
      createdOn:        formValue.createdOn
    };

    const wasCreate = !payload.id;
    this.disableActionBtn = true;
    this.smsCampaignConfigService.save(payload).subscribe({
      next: (savedId) => {
        this.uiEvent.displayUISuccess();
        this.disableActionBtn = false;
        if (wasCreate) {
          this.router.navigate(['/list-sms-campaigns']);
        } else {
          this.mainForm.patchValue({ id: savedId });
        }
      },
      error: err => {
        this.disableActionBtn = false;
        this.handleSaveError(err);
      }
    });
  }

  private handleSaveError(err: any) {
    displayError(err);
    const apiCode = err?.error?.code;
    const i18nKey = apiCode ? `errorCodes.${apiCode}` : null;
    const translated = i18nKey ? this.translateService.instant(i18nKey) : null;
    if (translated && translated !== i18nKey) {
      this.toastr.error(translated, this.translateService.instant('toast.errorTitle'));
    } else {
      this.uiEvent.displayUIError();
    }
  }
}
